import { Task, Comment } from '../types';
import { getTaskTags } from './taskUtils';

export const fetchTasksFromAsana = async (accessToken: string, userId: string): Promise<Task[]> => {
  try {
    // Get user's workspaces first
    const workspacesResponse = await fetch('https://app.asana.com/api/1.0/workspaces', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (workspacesResponse.ok) {
      const workspacesData = await workspacesResponse.json();
      
      if (workspacesData.data && workspacesData.data.length > 0) {
        const workspaceId = workspacesData.data[0].gid;
        
        // Use userId for assignee
        const tasksResponse = await fetch(
          `https://app.asana.com/api/1.0/tasks?workspace=${workspaceId}&assignee=${userId}&completed_since=now&opt_fields=name,created_at,due_on,projects.name,completed,html_url,parent.name,memberships.project.name,memberships.section.name,custom_fields`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        if (tasksResponse.ok) {
          const tasksData = await tasksResponse.json();

          // Add tags from localStorage and filter out completed tasks
          const processedTasks = tasksData.data
            .filter((task: any) => !task.completed)
            .map((task: any) => ({
              ...task,
              tags: getTaskTags(task.gid),
            }));

          return processedTasks;
        }
      }
    }
  } catch (error) {
    console.error('Failed to fetch tasks:', error);
  }

  return [];
};

export const fetchParentTasksWithSubtasks = async (accessToken: string, userId: string): Promise<Task[]> => {
  try {
    // Get user's workspaces first
    const workspacesResponse = await fetch('https://app.asana.com/api/1.0/workspaces', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (workspacesResponse.ok) {
      const workspacesData = await workspacesResponse.json();
      
      if (workspacesData.data && workspacesData.data.length > 0) {
        const workspaceId = workspacesData.data[0].gid;
        
        // Use userId for assignee
        const subtasksResponse = await fetch(
          `https://app.asana.com/api/1.0/tasks?workspace=${workspaceId}&assignee=${userId}&completed_since=now&opt_fields=name,created_at,due_on,projects.name,completed,html_url,parent.name,parent.gid,memberships.project.name,memberships.section.name,custom_fields`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        if (subtasksResponse.ok) {
          const subtasksData = await subtasksResponse.json();
          
          // Filter subtasks and get unique parent task IDs
          const subtasks = subtasksData.data.filter((task: any) => !task.completed && task.parent);
          const parentTaskIds: string[] = Array.from(new Set(subtasks.map((task: any) => task.parent.gid)));
          
          if (parentTaskIds.length === 0) {
            return [];
          }

          // Fetch parent tasks details
          const parentTasksPromises = parentTaskIds.map(async (parentId: string) => {
            const parentResponse = await fetch(
              `https://app.asana.com/api/1.0/tasks/${parentId}?opt_fields=name,created_at,due_on,projects.name,completed,html_url,memberships.project.name,memberships.section.name,custom_fields`,
              {
                headers: { Authorization: `Bearer ${accessToken}` },
              }
            );
            
            if (parentResponse.ok) {
              const parentData = await parentResponse.json();
              const parentSubtasks = subtasks.filter((task: any) => task.parent.gid === parentId);
              
              return {
                ...parentData.data,
                tags: getTaskTags(parentData.data.gid),
                hasSubtasksAssigned: true,
                subtaskCount: parentSubtasks.length,
                subtasks: parentSubtasks.map((subtask: any) => ({
                  gid: subtask.gid,
                  name: subtask.name,
                  projects: subtask.projects
                }))
              };
            }
            return null;
          });

          const parentTasks = (await Promise.all(parentTasksPromises)).filter(Boolean);
          return parentTasks;
        }
      }
    }
  } catch (error) {
    console.error('Failed to fetch parent tasks with subtasks:', error);
  }

  return [];
};

export const fetchMentionsFromAsana = async (accessToken: string, userId: string): Promise<Task[]> => {
  try {
    // Get user's workspaces first
    const workspacesResponse = await fetch('https://app.asana.com/api/1.0/workspaces', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (workspacesResponse.ok) {
      const workspacesData = await workspacesResponse.json();
      
      if (workspacesData.data && workspacesData.data.length > 0) {
        const workspaceId = workspacesData.data[0].gid;
        
        // Use userId for assignee
        const mentionsResponse = await fetch(
          `https://app.asana.com/api/1.0/tasks?workspace=${workspaceId}&assignee=${userId}&completed_since=now&opt_fields=name,created_at,due_on,completed`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        if (mentionsResponse.ok) {
          const mentionsData = await mentionsResponse.json();
          return mentionsData.data.filter((task: any) => !task.completed);
        }
      }
    }
  } catch (error) {
    console.error('Failed to fetch mentions:', error);
  }

  return [];
};

export const fetchPendingComments = async (
  accessToken: string,
  workspaceMembershipId: string,
  userId: string,
  setCommentsProgress: (progress: any) => void
): Promise<Comment[]> => {
  try {
    const comments: Comment[] = [];
    const processedTasks = new Set<string>();
    
    // Get user's workspaces first
    const workspacesResponse = await fetch('https://app.asana.com/api/1.0/workspaces', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (workspacesResponse.ok) {
      const workspacesData = await workspacesResponse.json();
      console.log('Workspaces:', workspacesData);
      
      if (workspacesData.data && workspacesData.data.length > 0) {
        const workspaceId = workspacesData.data[0].gid;
        console.log('Using workspace ID:', workspaceId);
        
        // Get all projects in the workspace
        const projectsResponse = await fetch(
          `https://app.asana.com/api/1.0/projects?workspace=${workspaceId}&opt_fields=name,archived`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        if (projectsResponse.ok) {
          const projectsData = await projectsResponse.json();
          
          // Filter out archived projects
          const activeProjects = projectsData.data.filter((project: any) => !project.archived);
          console.log('Found projects:', projectsData.data.length, `(${activeProjects.length} active, ${projectsData.data.length - activeProjects.length} archived)`);
          
          // Update progress for projects
          setCommentsProgress({ 
            current: 0, 
            total: activeProjects.length, 
            message: `Found ${activeProjects.length} active projects to search`,
            currentProject: '',
            currentTask: '',
            tasksInProject: 0,
            currentTaskIndex: 0
          });
          
          // Process projects in batches of 5 for concurrent API calls
          const batchSize = 5;
          for (let batchStart = 0; batchStart < activeProjects.length; batchStart += batchSize) {
            const batchEnd = Math.min(batchStart + batchSize, activeProjects.length);
            const projectBatch = activeProjects.slice(batchStart, batchEnd);
            
            console.log(`Processing batch ${Math.floor(batchStart / batchSize) + 1}: projects ${batchStart + 1}-${batchEnd}`);
            
            // Fetch tasks for all projects in this batch concurrently
            const projectTasksPromises = projectBatch.map(async (project: any) => {
              try {
                const projectTasksResponse = await fetch(
                  `https://app.asana.com/api/1.0/projects/${project.gid}/tasks?completed_since=now&opt_fields=name,html_url,assignee.name,completed,due_on`,
                  {
                    headers: { Authorization: `Bearer ${accessToken}` },
                  }
                );

                if (projectTasksResponse.ok) {
                  const projectTasksData = await projectTasksResponse.json();
                  
                  // Filter for incomplete tasks only and remove already processed tasks
                  const incompleteTasks = projectTasksData.data
                    .filter((task: any) => !task.completed)
                    .filter((task: any) => !processedTasks.has(task.gid));
                  
                  console.log(`Project "${project.name}" has ${incompleteTasks.length} new incomplete tasks (${projectTasksData.data.filter((t: any) => !t.completed).length} total)`);
                  
                  return {
                    project,
                    incompleteTasks,
                  };
                } else {
                  console.error('Failed to fetch tasks for project:', project.name, projectTasksResponse.status);
                  return { project, incompleteTasks: [] };
                }
              } catch (error) {
                console.error('Error fetching tasks for project:', project.name, error);
                return { project, incompleteTasks: [] };
              }
            });

            // Wait for all project tasks to be fetched
            const projectResults = await Promise.all(projectTasksPromises);
            
            // Process each project's tasks
            for (let i = 0; i < projectResults.length; i++) {
              const { project, incompleteTasks } = projectResults[i];
              const projectIndex = batchStart + i;
              
              // Update progress for current project (batch-level only)
              setCommentsProgress({ 
                current: projectIndex + 1, 
                total: activeProjects.length, 
                message: `Searching project "${project.name}" (${incompleteTasks.length} new tasks)`,
                currentProject: project.name,
                currentTask: '',
                tasksInProject: incompleteTasks.length,
                currentTaskIndex: 0
              });
              
              // Process tasks in batches of 5 for concurrent comment fetching
              for (let taskBatchStart = 0; taskBatchStart < incompleteTasks.length; taskBatchStart += batchSize) {
                const taskBatchEnd = Math.min(taskBatchStart + batchSize, incompleteTasks.length);
                const taskBatch = incompleteTasks.slice(taskBatchStart, taskBatchEnd);
                
                // Update progress for current task batch
                setCommentsProgress({ 
                  current: projectIndex + 1, 
                  total: activeProjects.length, 
                  message: `Processing tasks in batch ${Math.floor(taskBatchStart / batchSize) + 1} of ${Math.ceil(incompleteTasks.length / batchSize)}`,
                  currentProject: project.name,
                  currentTask: `Batch ${Math.floor(taskBatchStart / batchSize) + 1}`,
                  tasksInProject: incompleteTasks.length,
                  currentTaskIndex: taskBatchStart + taskBatch.length
                });
                
                // Fetch comments for all tasks in this batch concurrently
                const taskCommentsPromises = taskBatch.map(async (task: any, taskIndex: number) => {
                  // Mark this task as processed
                  processedTasks.add(task.gid);
                  
                  // Update progress for individual task
                  setCommentsProgress((prev: any) => ({ 
                    ...prev,
                    currentTask: task.name,
                    currentTaskIndex: taskBatchStart + taskIndex + 1
                  }));
                  
                  try {
                    const commentsResponse = await fetch(
                      `https://app.asana.com/api/1.0/tasks/${task.gid}/stories?opt_fields=text,created_at,created_by.name,created_by.gid`,
                      {
                        headers: { Authorization: `Bearer ${accessToken}` },
                      }
                    );

                    if (commentsResponse.ok) {
                      const commentsData = await commentsResponse.json();
                      const commentStories = commentsData.data;
                      
                      console.log(`Task "${task.name}" has ${commentStories.length} comments`);

                      const taskComments: Comment[] = [];
                      
                      for (const comment of commentStories) {
                        // Skip comments without a created_by field (system comments)
                        if (!comment.created_by) {
                          continue;
                        }
                        
                        // Check for mentions in multiple formats:
                        // 1. Direct workspace membership ID
                        // 2. Profile URL with user ID
                        // 3. Direct user ID mention
                        const isMention = comment.text.includes(workspaceMembershipId) || 
                                         comment.text.includes(`profile/${userId}`) ||
                                         comment.text.includes(userId);
                        
                        // Debug: Log mention detection
                        if (isMention) {
                          console.log('Potential mention found in comment:', {
                            commentText: comment.text,
                            workspaceMembershipId: workspaceMembershipId,
                            userId: userId,
                            isMention: isMention
                          });
                        }
                        
                        if (isMention) {
                          // Check if the current user has replied to this comment (using userId)
                          const commentIndex = commentStories.indexOf(comment);
                          const laterComments = commentStories.slice(commentIndex + 1);
                          const hasReplied = laterComments.some((laterComment: any) => 
                            laterComment.created_by && laterComment.created_by.gid === userId
                          );
                          
                          if (!hasReplied) {
                            // Debug: Log task URL
                            console.log('Task URL for comment:', {
                              taskName: task.name,
                              taskGid: task.gid,
                              htmlUrl: task.html_url,
                              hasHtmlUrl: !!task.html_url,
                              workspaceMembershipId: workspaceMembershipId
                            });
                            
                            taskComments.push({
                              gid: comment.gid,
                              text: comment.text,
                              created_at: comment.created_at,
                              created_by: comment.created_by,
                              task_gid: task.gid,
                              task_name: task.name,
                              task_url: task.html_url ? `${task.html_url}/f` : `https://app.asana.com/0/${task.gid}/${task.gid}/f`,
                              task_assignee: task.assignee?.name,
                              is_mention: isMention,
                              is_replied: false
                            });
                          }
                        }
                      }
                      
                      return taskComments;
                    } else {
                      console.error('Failed to fetch comments for task:', task.name, commentsResponse.status);
                      return [];
                    }
                  } catch (error) {
                    console.error('Error fetching comments for task:', task.name, error);
                    return [];
                  }
                });

                // Wait for all task comments to be fetched and add to results
                const taskCommentsResults = await Promise.all(taskCommentsPromises);
                taskCommentsResults.forEach(taskComments => {
                  comments.push(...taskComments);
                });
              }
            }
          }
        } else {
          console.error('Failed to fetch projects:', projectsResponse.status, projectsResponse.statusText);
        }
      }
    } else {
      console.error('Failed to fetch workspaces:', workspacesResponse.status, workspacesResponse.statusText);
    }
    
    console.log('Total comments found:', comments.length);
    console.log('Total unique tasks processed:', processedTasks.size);
    return comments;
  } catch (error) {
    console.error('Failed to fetch pending comments:', error);
    return [];
  }
}; 